import { Container, Graphics, Sprite, Texture } from "pixi.js";
import { lerpPalette, paletteForIntensity, toCss, type Palette } from "./palette";

const RAY_COUNT = 26;

/** Reactive sky: gradient + radial ray burst + star field, tinted by intensity. */
export class Background {
  readonly container = new Container();
  private readonly skyCanvas: HTMLCanvasElement;
  private readonly skyCtx: CanvasRenderingContext2D;
  private readonly skySprite: Sprite;
  private readonly rays = new Graphics();
  private readonly stars = new Container();
  private readonly starDots: { g: Graphics; phase: number; speed: number }[] = [];

  private current: Palette = paletteForIntensity(0.2);
  private target: Palette = this.current;
  private blend = 1;
  private width = 0;
  private height = 0;
  private time = 0;

  constructor() {
    this.skyCanvas = document.createElement("canvas");
    this.skyCanvas.width = 4;
    this.skyCanvas.height = 256;
    this.skyCtx = this.skyCanvas.getContext("2d")!;
    this.skySprite = new Sprite(Texture.from(this.skyCanvas));
    this.container.addChild(this.skySprite, this.rays, this.stars);
    this.paintSky(this.current);
  }

  resize(width: number, height: number): void {
    this.width = width;
    this.height = height;
    this.skySprite.width = width;
    this.skySprite.height = height;
    this.rays.position.set(width / 2, height + 40);
    this.buildStars();
    this.drawRays(this.current);
  }

  setIntensity(intensity: number): void {
    const next = paletteForIntensity(intensity);
    if (next.skyMid !== this.target.skyMid || next.skyTop !== this.target.skyTop) {
      this.current = lerpPalette(this.current, this.target, Math.min(1, this.blend));
      this.target = next;
      this.blend = 0;
    }
  }

  get palette(): Palette {
    return lerpPalette(this.current, this.target, Math.min(1, this.blend));
  }

  update(dtSeconds: number): void {
    this.time += dtSeconds;
    if (this.blend < 1) {
      this.blend = Math.min(1, this.blend + dtSeconds / 1.8);
      const p = this.palette;
      this.paintSky(p);
      this.drawRays(p);
      for (const s of this.starDots) s.g.tint = p.star;
    }
    this.rays.rotation = Math.sin(this.time * 0.05) * 0.05;
    for (const s of this.starDots) {
      s.g.alpha = 0.35 + 0.55 * (0.5 + 0.5 * Math.sin(this.time * s.speed + s.phase));
    }
  }

  private paintSky(p: Palette): void {
    const ctx = this.skyCtx;
    const grad = ctx.createLinearGradient(0, 0, 0, 256);
    grad.addColorStop(0, toCss(p.skyTop));
    grad.addColorStop(0.55, toCss(p.skyMid));
    grad.addColorStop(1, toCss(p.skyBottom));
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, 4, 256);
    this.skySprite.texture.source.update();
  }

  private drawRays(p: Palette): void {
    const g = this.rays;
    g.clear();
    const radius = Math.hypot(this.width, this.height) * 1.2;
    const full = Math.PI; // fan across the top half
    const step = full / RAY_COUNT;
    for (let i = 0; i < RAY_COUNT; i++) {
      if (i % 2 === 0) continue;
      const a0 = Math.PI + i * step;
      const a1 = a0 + step * 0.62;
      g.moveTo(0, 0);
      g.arc(0, 0, radius, a0, a1);
      g.lineTo(0, 0);
      g.fill({ color: p.ray, alpha: 0.07 });
    }
  }

  private buildStars(): void {
    for (const s of this.starDots) s.g.destroy();
    this.starDots.length = 0;
    this.stars.removeChildren();
    const count = Math.round((this.width * this.height) / 9000);
    for (let i = 0; i < count; i++) {
      const g = new Graphics();
      const r = Math.random() < 0.85 ? 1 + Math.random() : 2 + Math.random() * 1.5;
      g.circle(0, 0, r).fill({ color: 0xffffff });
      g.position.set(Math.random() * this.width, Math.random() * this.height * 0.85);
      g.tint = this.palette.star;
      this.stars.addChild(g);
      this.starDots.push({ g, phase: Math.random() * Math.PI * 2, speed: 0.4 + Math.random() * 1.6 });
    }
  }
}
